import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import Modal from "../Modal/Modal";
// import Tooltip from "../Tooltip/Tooltip";
// import { DiamondIcon } from "../Icons/DiamondIcon";
import IconButton from "../Button/IconButton";
import { pages } from "../../lib/routeUtils";
import { NotEnoughCredits } from "../../types/notEnoughCredits";
import { getProfile } from "../../redux/reducers/profileReducer";
import { getPrefilled } from "../../redux/reducers/popupsReducer";
import { Popups, updatePopup } from "../../redux/actions/popupsActions";
import { FullScreenPopupContentWrapper, FullScreenPopupTitle } from "./popups.styles";
import PopupHeader from "./components/PopupHeader";
import useCustomAvatarPopupSteps from "../../hooks/useCustomAvatarPopupSteps";

export enum CustomAvatar {
  InstantAvatar = "Instant Avatar",
  TalkingImage = "Talking Image",
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const SelectTypeCustomAvatarPopup = ({ open, onClose }: Props) => {
  const { openTalkingImageUploader, openInstantImageGuidePopup } = useCustomAvatarPopupSteps(onClose);
  const dispatch = useDispatch();
  const isFirstRender = useRef(true);
  const { pathname } = useLocation();
  const { allowToCloneAIHuman = false } = useSelector(getProfile);
  const { noRefresh, prefilledType } = useSelector(getPrefilled);
  const [selectedType, setSelectedType] = useState<CustomAvatar | null>(prefilledType);

  const handleSelectedTypeChange = (type: CustomAvatar) => {
    setSelectedType((prevSelectedType) => {
      if (prevSelectedType === type) {
        return null;
      }
      return type;
    });
  };

  const handleSubmit = () => {
    if (!selectedType) return;
    if (selectedType === CustomAvatar.TalkingImage) {
      openTalkingImageUploader();
    } else {
      openInstantImageGuidePopup();
    }
  };

  const upgradeYourPlan = () => {
    const prefilled: NotEnoughCredits = {
      title: "Upgrade the plan",
      description: "Get access to additional premium features",
      link: `${pages.pricingSubscriptions()}?plan=humans`,
    };

    dispatch(
      updatePopup({
        popup: Popups.notEnoughCreditsPopup,
        status: true,
        prefilled,
      }),
    );
  };

  useEffect(() => {
    if (!isFirstRender.current) {
      onClose();
    }

    isFirstRender.current = false;
  }, [pathname]);

  return (
    <Wrapper>
      <Modal className="selectTypeOfCustomAvatarPopup" open={open} closeIcon={false} onClose={onClose}>
        <PopupHeader
          menuButtonAction={onClose}
          handleNextButton={handleSubmit}
          disableNextButton={!selectedType || selectedType === CustomAvatar.InstantAvatar}
          noPreviousButton
        />
        <FullScreenPopupContentWrapper>
          <FullScreenPopupTitle>Select type of custom avatar</FullScreenPopupTitle>
          <TypesWrapper>
            {menu.map((item, index) => {
              const isDisabled = (!allowToCloneAIHuman && item.title === CustomAvatar.InstantAvatar) || item.disabled;
              return (
                <Type
                  key={item.title}
                  isActive={item.title === selectedType}
                  isDisabled={isDisabled}
                  onClick={() => !isDisabled && handleSelectedTypeChange(item.title)}
                >
                  <Image src={item.imageSrc} alt="" isDisabled={isDisabled} />
                  <TypeContentBox isActive={item.title === selectedType}>
                    <TypeTitle>{item.title}</TypeTitle>
                    <TypeDescription>{item.description}</TypeDescription>
                  </TypeContentBox>
                  {/* {isDisabled && (
                  <>
                    <DiamondButton icon={<DiamondIcon />} onClick={upgradeYourPlan} />
                    <Tooltip
                      className="select-type-custom-avatar-tooltip"
                      text={
                        <span>
                          <b onClick={upgradeYourPlan}>Upgrade plan to access Instant Custom Avatars</b>
                        </span>
                      }
                      position="bottom"
                      reverseColor
                      arrow
                    />
                  </>
                )} */}
                </Type>
              );
            })}
          </TypesWrapper>
        </FullScreenPopupContentWrapper>
        {/* <PopupFooterButtonsWrapper>
          <Button buttonTheme={ButtonThemes.White} onClick={onClose} text="Cancel" />
          <Button disabled={!selectedType} onClick={handleSubmit} text="Submit" />
        </PopupFooterButtonsWrapper> */}
      </Modal>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  /* .modal-wrapper {
    max-height: 100vh;
    max-width: 100%;
  } */
  /* .modal-darkener {
    background: none;
    backdrop-filter: none;
  } */
`;

const menu = [
  {
    title: CustomAvatar.InstantAvatar,
    description: "Upload your video to create your custom avatar",
    imageSrc: "/images/ai-humans/instant-avatar.png",
    disabled: true,
  },
  {
    title: CustomAvatar.TalkingImage,
    description: "Transform any image into a talking avatar",
    imageSrc: "/images/ai-humans/talking-image.png",
    disabled: false,
  },
];

const TypesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(300px, 514px));
  column-gap: 40px;
  row-gap: 28px;
  justify-content: center;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const Type = styled.div<{ isActive?: boolean; isDisabled?: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  /* background: #1d1f23; */
  height: 378px;
  width: 100%;
  max-width: 514px;

  cursor: pointer;

  .select-type-custom-avatar-tooltip {
    box-shadow: none;
    border-radius: 8px;
    top: 110%;
    width: 100%;
    max-width: 328px;

    span {
      font-size: 12px !important;
      line-height: 16px !important;
    }
  }

  &:hover > .select-type-custom-avatar-tooltip {
    opacity: 1;
    visibility: visible;
  }

  /* ${({ isActive, theme }) =>
    isActive &&
    `
        background: #292f3d;
    `} */

  ${({ isDisabled }) =>
    isDisabled &&
    `
        & > div, & > span {
          opacity: 0.4;
        }
    `}
`;

const TypeContentBox = styled.div<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  height: 76px;
  background: ${({ theme, isActive }) => (isActive ? `${theme.activeMenu}4D` : theme.primaryBackground1)};

  & > div {
    width: 16px;
    margin-bottom: 1px;
    pointer-events: none;
  }

  /* & label:before {
    background: ${({ theme }) => theme.blueDark};
    box-shadow: none;
  }
  & input:checked + label:before {
    border: none;
    box-shadow: none;
    width: 16px;
    height: 16px;
    background: ${({ theme }) => theme.blueDark};
  } */
`;
const Image = styled.img<{ isDisabled: boolean }>`
  height: 100%;
  max-height: 302px;
  width: 100%;
  object-fit: cover;

  ${({ isDisabled }) =>
    isDisabled &&
    `
        filter: grayscale(100%);
    `}
`;

const TypeTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ theme }) => theme.white};
`;

const TypeDescription = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ theme }) => theme.white};
`;

const DiamondButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  right: 16px;
  width: 48px;
  height: 48px;
  max-width: 48px;
  transform: translateY(-50%);
  pointer-events: auto;
`;

export default SelectTypeCustomAvatarPopup;
